import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  ApexNonAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexResponsive,
  ApexFill,
  ApexLegend,
  ApexDataLabels,
  ApexPlotOptions
} from 'ng-apexcharts';
import { PieChartService, ChartData } from './pie-chart.service';

interface CategoryColor {
  category: string;
  color: string;
}

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: string[];
  fill: ApexFill;
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
};

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss']
})
export class PieChartComponent implements OnInit {
  @ViewChild('chart') chart!: ChartComponent;
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  @Input() colorStrategy: 'base' | 'hsl' | 'category' | 'gradient' | 'theme' = 'base';
  @Input() colorTheme: 'green' | 'blue' | 'purple' | 'orange' | 'red' = 'green';
  @Input() gradientStartColor: string = '#1B5E20';
  @Input() gradientEndColor: string = '#E8F5E9';


  startDate: string = '';
  endDate: string = '';

  private baseColors = [
    '#1B5E20', '#B6D1C1', '#E8F5E9', '#4CAF50', '#81C784', '#A5D6A7', '#C8E6C9'
  ];

  // Getter public untuk baseColors agar bisa diakses dari luar
  public get getBaseColors(): string[] {
    return this.baseColors;
  }

  // Method public untuk mendapatkan warna berdasarkan index
  public getColorByIndex(index: number): string {
    return this.baseColors[index % this.baseColors.length];
  }

  @Output() colorsGenerated = new EventEmitter<CategoryColor[]>();

  constructor(
    private pieChartService: PieChartService
  ) { }

  ngOnInit(): void {
    this.loadChartData();

    const startDate = localStorage.getItem('startDate')
    const endDate = localStorage.getItem('endDate')

    this.startDate = startDate ? startDate : '';
    this.endDate = endDate ? endDate : '';
  }

  public chartOptions: ChartOptions = {
    series: [0], // Default series
    labels: ['Loading...'], // Default label
    chart: {
      type: 'donut',
      width: 300,
      height: 300,
      background: 'transparent'
    },
    plotOptions: {
      pie: {
        donut: {
          size: '66%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '30px',
            },
            total: {
              show: true,
              label: 'Total',
              color: '#373d3f',
              fontSize: '22px',
              fontFamily: 'Arial, sans-serif',
              fontWeight: 700,
              formatter: function () {
                return '0';
              }
            }
          }
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number, opts) {
        return opts.w.globals.series[opts.seriesIndex].toString();
      },
      style: {
        fontSize: '18',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 500
      }
    },
    fill: {
      colors: [] // Will be populated dynamically
    },
    legend: {
      show: false,
      position: 'bottom',
      fontSize: '30',
      fontFamily: 'Arial, sans-serif',
      markers: {
        strokeWidth: 0,
        offsetX: -5
      },
      itemMargin: {
        horizontal: 15,
        vertical: 5
      }
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 280
          },
          legend: {
            position: 'bottom'
          }
        }
      }
    ]
  };

  private loadChartData(): void {
    this.isLoading = true;
    this.hasError = false;

    this.pieChartService.getChartData(this.startDate, this.endDate).subscribe({
      next: (data: ChartData[]) => {
        if (data && data.length > 0) {
          const total = this.pieChartService.calculateTotal(data);
          this.updateChartWithData(data, total);
        } else {
          this.handleEmptyData();
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading chart data:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to load chart data. Please try again.';
        this.isLoading = false;
      }
    });
  }

  // private updateChartWithData(data: ChartData[], total: number): void {
  //   // Update chart series and labels
  //   this.chartOptions.series = data.map(item => item.value);
  //   this.chartOptions.labels = data.map(item => item.category);

  //   // Generate dynamic colors based on data length
  //   this.chartOptions.fill = {
  //     colors: this.generateDynamicColors(data.length)
  //   };

  //   // Update total formatter
  //   if (this.chartOptions.plotOptions?.pie?.donut?.labels?.total) {
  //     this.chartOptions.plotOptions.pie.donut.labels.total.formatter = () => total.toString();
  //   }

  //   // Trigger chart update if chart is available
  //   if (this.chart) {
  //     this.chart.updateOptions(this.chartOptions);
  //   }
  // }

  // Method untuk generate colors secara dinamis


  private generateDynamicColors(dataLength: number): string[] {
    const colors: string[] = [];

    // Strategy 1: Jika data lebih sedikit dari base colors, gunakan base colors
    if (dataLength <= this.baseColors.length) {
      return this.baseColors.slice(0, dataLength);
    }

    // Strategy 2: Jika data lebih banyak, generate additional colors
    for (let i = 0; i < dataLength; i++) {
      if (i < this.baseColors.length) {
        colors.push(this.baseColors[i]);
      } else {
        // Generate additional colors using HSL
        colors.push(this.generateHSLColor(i));
      }
    }

    return colors;
  }

  // Method untuk generate HSL colors
  private generateHSLColor(index: number): string {
    // Generate colors dengan hue yang berbeda, saturation dan lightness yang konsisten
    const hue = (index * 137.508) % 360; // Golden angle approximation untuk distribusi yang baik
    const saturation = 65 + (index % 3) * 10; // Variasi saturation 65-85%
    const lightness = 45 + (index % 4) * 8; // Variasi lightness 45-69%

    return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
  }

  // Alternative method untuk generate colors berdasarkan category name
  private generateColorByCategory(category: string): string {
    // Generate color berdasarkan hash dari nama category
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 55%)`;
  }

  // Method untuk generate gradient colors
  private generateGradientColors(startColor: string, endColor: string, steps: number): string[] {
    const colors: string[] = [];

    // Convert hex to RGB
    const start = this.hexToRgb(startColor);
    const end = this.hexToRgb(endColor);

    if (!start || !end) return this.baseColors.slice(0, steps);

    for (let i = 0; i < steps; i++) {
      const ratio = i / (steps - 1);
      const r = Math.round(start.r + (end.r - start.r) * ratio);
      const g = Math.round(start.g + (end.g - start.g) * ratio);
      const b = Math.round(start.b + (end.b - start.b) * ratio);

      colors.push(`rgb(${r}, ${g}, ${b})`);
    }

    return colors;
  }

  // Helper method untuk convert hex to RGB
  private hexToRgb(hex: string): { r: number, g: number, b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  private handleEmptyData(): void {
    this.chartOptions.series = [1];
    this.chartOptions.labels = ['No Data'];

    if (this.chartOptions.plotOptions?.pie?.donut?.labels?.total) {
      this.chartOptions.plotOptions.pie.donut.labels.total.formatter = () => '0';
    }
  }

  // Method untuk refresh data (bisa dipanggil dari parent component)
  public refreshData(startDate?: string, endDate?: string): void {
    this.startDate = startDate ?? '';
    this.endDate = endDate ?? '';
    this.loadChartData();
  }

  private updateChartWithData(data: ChartData[], total: number): void {
    // Update chart series and labels
    this.chartOptions.series = data.map(item => item.value);
    this.chartOptions.labels = data.map(item => item.category);

    // Generate dynamic colors based on data length
    const generatedColors = this.generateDynamicColors(data.length);
    this.chartOptions.fill = {
      colors: generatedColors
    };

    // Update total formatter
    if (this.chartOptions.plotOptions?.pie?.donut?.labels?.total) {
      this.chartOptions.plotOptions.pie.donut.labels.total.formatter = () => total.toString();
    }

    // Trigger chart update if chart is available
    if (this.chart) {
      this.chart.updateOptions(this.chartOptions);
    }

    // Create color mapping untuk dikirim ke parent component
    // PENTING: Emit setelah chart di-update
    const colorMapping: CategoryColor[] = data.map((item, index) => ({
      category: item.category,
      color: generatedColors[index]
    }));

    // console.log('Emitting color mapping:', colorMapping);

    // Emit color mapping ke parent component dengan delay kecil
    setTimeout(() => {
      this.colorsGenerated.emit(colorMapping);
    }, 100);
  }

  // Method untuk mendapatkan warna berdasarkan category name
  public getColorByCategory(categoryName: string): string {
    // Cari index category dalam chartOptions.labels
    const categoryIndex = this.chartOptions.labels.findIndex(
      label => label.toLowerCase() === categoryName.toLowerCase()
    );

    if (categoryIndex !== -1 && this.chartOptions.fill?.colors) {
      return this.chartOptions.fill.colors[categoryIndex];
    }

    // Fallback ke warna default jika tidak ditemukan
    return this.baseColors[0];
  }

  // Method untuk force emit current colors (untuk debugging)
  public emitCurrentColors(): void {
    if (this.chartOptions.labels && this.chartOptions.fill?.colors) {
      const colorMapping: CategoryColor[] = this.chartOptions.labels.map((label, index) => ({
        category: label,
        color: this.chartOptions.fill!.colors![index]
      }));

      // console.log('Force emitting colors:', colorMapping);
      this.colorsGenerated.emit(colorMapping);
    }
  }
}
